import { isLocallySignedOutAtom } from "@features/Authentication/atoms";
import { useHandleLogOutCleanUp } from "@features/Authentication/hooks/useHandleLogOutCleanUp";
import { sessionAtom, sessionQueryVersionAtom } from "@features/Session";
import { t } from "@lingui/macro";
import * as Sentry from "@sentry/react-native";
import graphql from "babel-plugin-relay/macro";
import Constants from "expo-constants";
import { useAtomValue, useSetAtom } from "jotai";
import { useEffect } from "react";
import { Platform } from "react-native";
import { PreloadedQuery, usePreloadedQuery } from "react-relay";

import { buildStaffAppUrl } from "@/utils/getStaffAppUrlString";
import { useFlags } from "@/utils/launchDarkly";
import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";
import { Box } from "@whoosh/design-system/src/components/primitive";

import {
  clearPersistedNativeSession,
  reconcileBiometricUser,
} from "../../features/BiometricAuthentication/biometricUnlock";
import { BaseNavigator } from "../BaseNavigator/index";
import { SessionCheckerQueryHandlerQuery } from "./__generated__/SessionCheckerQueryHandlerQuery.graphql";

export const SessionCheckerQueryHandler = ({
  sessionRef,
  queryVersion,
}: {
  queryVersion: number;
  sessionRef: PreloadedQuery<SessionCheckerQueryHandlerQuery>;
}) => {
  const { showToast } = useToast();
  const { biometricAuth } = useFlags();
  const handleLogOut = useHandleLogOutCleanUp();
  const setSession = useSetAtom(sessionAtom);
  const isLocallySignedOut = useAtomValue(isLocallySignedOutAtom);
  const setIsLocallySignedOut = useSetAtom(isLocallySignedOutAtom);
  const currentSessionQueryVersion = useAtomValue(sessionQueryVersionAtom);
  const isStaleQuery = queryVersion !== currentSessionQueryVersion;

  const isLocalApp = () => {
    const configDeployEnv = Constants?.expoConfig?.extra?.eas?.deployEnv;
    return configDeployEnv === "local";
  };

  const data = usePreloadedQuery<SessionCheckerQueryHandlerQuery>(
    SessionCheckerQueryHandlerQueryString,
    sessionRef
  );

  /**
   * If someone who isn't a member tries to log into the member app
   * ship them back to the staff app, or, if we are on the mobile build,
   * just loop them to the login screen and show them a toast letting them know
   * staff cannot log into the mobile app
   * TODO: this can probably be moved to a hook or something and not loose in this component
   */
  const user = isLocallySignedOut || isStaleQuery ? null : data.session?.user;

  useEffect(() => {
    if (biometricAuth === true && Platform.OS !== "web" && user?.id) {
      void reconcileBiometricUser(user.id).catch((error: unknown) => {
        Sentry.captureException(error);
        setIsLocallySignedOut(true);
        void clearPersistedNativeSession().catch(Sentry.captureException);
      });
    }
  }, [biometricAuth, user?.id, setIsLocallySignedOut]);

  if (user) {
    if (data.session?.user?.__typename !== "MemberUser") {
      if (Platform.OS === "web") {
        if (isLocalApp()) {
          handleLogOut();
        } else {
          window.location.href = buildStaffAppUrl(window.location);
        }
      } else {
        handleLogOut();
        setTimeout(() => {
          showToast({
            description: t`Whoops! For now, only members can log into the mobile application.`,
            semanticStyle: "error",
            toastId: "staff-login-error",
            testID: "lock-slot-error-toast",
          });
        }, 200);
      }
    }
  }

  const userClubs =
    user?.__typename === "MemberUser"
      ? user.memberProfiles?.edges?.map((edge) => ({
          slug: edge?.node?.member?.club?.slug || "",
        })) || []
      : [];

  if (!isStaleQuery && user) {
    setSession({
      user: {
        id: user.id,
        email: user.email!,
        username: user.username!,
        clubs:
          user.memberProfiles?.edges?.map((edge) => ({
            id: edge?.node?.member?.club?.id || "",
            slug: edge?.node?.member?.club?.slug || "",
            name: edge?.node?.member?.club?.name || "",
            logoImage: edge?.node?.member?.club?.logoImage?.publicId || "",
          })) || [],
      },
    });
  } else if (!isStaleQuery) {
    setSession(null);
  }
  return (
    <Box flex={1}>
      <BaseNavigator hasSession={!!user} userClubs={userClubs} />
    </Box>
  );
};

export const SessionCheckerQueryHandlerQueryString = graphql`
  query SessionCheckerQueryHandlerQuery {
    session {
      user {
        __typename
        id
        email
        username
        ... on MemberUser {
          memberProfiles(active: true) {
            edges {
              node {
                id
                member {
                  club {
                    id
                    name
                    slug
                    logoImage {
                      # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
                      publicId: renderingKey
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;
